<template>
  <Teleport to="#modals">
    <Transition
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="showBackground"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        tabindex="-1"
      >
        <!-- Backdrop -->
        <div
          class="absolute inset-0 bg-black/50"
        />

        <!-- Popup Container -->
        <Transition
          enter-active-class="transition-all duration-300 ease-out"
          enter-from-class="opacity-0 scale-95 translate-y-4"
          enter-to-class="opacity-100 scale-100 translate-y-0"
          leave-active-class="transition-all duration-200 ease-in"
          leave-from-class="opacity-100 scale-100 translate-y-0"
          leave-to-class="opacity-0 scale-95 translate-y-4"
        >
          <div
            v-if="showContent"
            class="relative w-full max-w-sm max-h-[90vh] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
            @mousedown.stop=""
          >
            <!-- Header -->
            <div class="flex items-center justify-between px-6 py-4 bg-white">
              <div class="flex items-center space-x-3">
                <slot name="icon">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"  class="w-5 h-5 text-gray-600">
    <path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
</svg>
                </slot>

                <h2
                  :id="titleId"
                  class="text-lg font-semibold text-gray-900"
                >
                  <slot name="header">
                    Popup Title
                  </slot>
                </h2>
              </div>

              <button
                class="btn-icon btn-icon-red"
                type="button"
                aria-label="Close popup"
                @click.prevent="emitClose"
              >
                <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-5 h-5">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M6 18L18 6M6 6l12 12"
    />
</svg>
              </button>
            </div>

            <!-- Body -->
            <div class="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <slot name="body">
                <div class="text-gray-600">
                  Popup content goes here
                </div>
              </slot>
            </div>

            <!-- Footer -->
            <div
              v-if="$slots.footer"
              class="flex items-center justify-end space-x-3 px-6 py-4 bg-white"
            >
              <slot name="footer" />
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { onKeyStroke } from '@vueuse/core';
import { computed, nextTick, onMounted, ref } from 'vue';

const emits = defineEmits<{
  close: [];
}>();

const titleId = computed(() => `popup-title-${Math.random().toString(36).substr(2, 9)}`);
const showBackground = ref(false);
const showContent = ref(false);

onMounted(() => {
  showBackground.value = true;

  nextTick(() => {
    showContent.value = true;
  });
});

const closePopup = () => {
  showContent.value = false;

  nextTick(() => {
    showBackground.value = false;

    nextTick(() => {
      emits('close');
    });
  });
};

// Handle escape key
onKeyStroke('Escape', () => {
  closePopup();
});

// Emit close event
const emitClose = () => {
  closePopup();
};

// Expose methods for parent components
defineExpose({
  close: closePopup,
});
</script>
